import {
    NetworkError,
    generateRouteDefinition,
    generateRouteHandler,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeBrowseDetailPageIntent,
    type BrowseDetailPageIntent,
} from './BrowseDetailPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { Page } from '@jet-app/music-classical/models';
import { IntentKind } from '../../common';
import { renderBrowseDetailPage } from './renderBrowseDetailPage';
import { requestEntitiesList } from '@jet-app/music-classical/api/request-entities-list';
import type { ClassicalListViewPage } from '@jet-app/music-classical/api/types';
import { makeBrowseDetailPaginationIntent } from './paginate/BrowseDetailPaginationIntent';
import { pageSortByChangeEventTrigger } from '../../common/page-invalidation-rules';
import { requestViewPageWithQuery } from '@jet-app/music-classical/api/request-view-page';

const canonicalUrlRule = {
    path: '/{storefront}/browse/{entityType}',
    query: [],
};

// This will be used for Visiting a Browse Detail Page Directly from the URL
// Help in SEO URLs for each browse detail page
// Playlists are excluded from this list as they have their own page controller
const ENTITY_TYPE_TO_API_PATH_MAP = {
    composers: 'browse/composer/composers',
    periods: 'periods',
    genres: 'genres',
    conductors: 'browse/conductor/artists',
    orchestras: 'browse/orchestra/artists',
    soloists: 'browse/soloist/artists',
    ensembles: 'browse/ensemble/artists',
    choirs: 'browse/choir/artists',
};
const browseDetailEntityKeys = Object.keys(ENTITY_TYPE_TO_API_PATH_MAP).join(
    '|',
);
const browseKeyWithEntityPattern = new RegExp(
    `(?:http)s?:\/\/(?:classical\.music)\.apple\.com\/(?:(?<storefront>\\w{2})\\/)?browse\/(?<entity>${browseDetailEntityKeys})\/?(\/?.*)?$`,
);
const browseDetailPagePatterns: RegExp[] = [browseKeyWithEntityPattern];

interface MatchGroups {
    storefront?: string;
    entity?: string;
}

export const BrowseDetailPageIntentController: IntentController<BrowseDetailPageIntent> &
    RouteProvider = {
    $intentKind: IntentKind.BROWSE_DETAIL_PAGE,

    routes: function (objectGraph: ObjectGraph) {
        return [
            {
                rules: [
                    {
                        regex: browseDetailPagePatterns,
                        query: ['l?'],
                    },
                ],
                handler: (url, handlerParams) => {
                    const regexParameters: MatchGroups =
                        url.toString().match(browseKeyWithEntityPattern)
                            ?.groups ?? {};
                    return generateRouteHandler(
                        makeBrowseDetailPageIntent,
                        objectGraph,
                    ).apply(this, [
                        url,
                        {
                            ...handlerParams,
                            entityType: regexParameters.entity,
                            storefront: regexParameters.storefront,
                        },
                    ]);
                },
            },
        ];
    },

    async perform(
        intent: BrowseDetailPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<Page> {
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );
        const { entityPath, entityType, metadata } = intent;
        let apiResponse;

        if (metadata?.url) {
            apiResponse = await requestViewPageWithQuery(
                intent.metadata.url,
                objectGraph,
            );
        } else {
            const entityTypeBasedPath =
                ENTITY_TYPE_TO_API_PATH_MAP[entityType] ||
                `instrument/${entityType}`;
            apiResponse = await requestEntitiesList(
                entityPath ?? entityTypeBasedPath,
                objectGraph,
            );
        }

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        const nextPageURL = (apiResponse.body as ClassicalListViewPage)
            .firstPage?.next;

        const page = renderBrowseDetailPage(
            canonicalURL,
            apiResponse,
            entityType,
            objectGraph,
        );

        return {
            ...page,
            nextIntent: nextPageURL
                ? makeBrowseDetailPaginationIntent({
                      url: nextPageURL,
                      id: page?.sections?.[0]?.id,
                  })
                : null,
            invalidationRules: {
                eventTriggers: [pageSortByChangeEventTrigger(intent)],
            },
        };
    },
};
